import { GifsResult } from '@giphy/js-fetch-api'
import { ForwardedRef, ReactNode, forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Position } from 'types'
import { PortalTooltipContext } from 'ui'
import { ArtboardRef } from '../artboard-gl/types'
import Artboard from './artboard'
import Editor from './editors'
import ContextManager from './manager'
import Navigation, { CloseButton } from './navigation'
import Overlay from './overlays'
import { Container, Content, Panel } from './style'
import { UGEEditor, UGEGAEventCallback, UGERef } from './types'

export type UGEProps = {
    apiKey: string
    children?: ReactNode
    className?: string
    closeable?: boolean
    defaultEditor?: UGEEditor
    enableStickers?: boolean
    source: File | string
    fetchFavoriteStickers?: (offset: number) => Promise<GifsResult>
    onClose?: () => void
    onGAEvent: UGEGAEventCallback
}

/**
 Universal GIPHY Editor

██╗   ██╗ ██████╗ ███████╗
██║   ██║██╔════╝ ██╔════╝
██║   ██║██║  ███╗█████╗  
██║   ██║██║   ██║██╔══╝  
╚██████╔╝╚██████╔╝███████╗
 ╚═════╝  ╚═════╝ ╚══════╝
 */
const UGE = (
    {
        apiKey,
        children,
        className,
        closeable = false,
        defaultEditor,
        enableStickers = false,
        source,
        fetchFavoriteStickers,
        onClose,
        onGAEvent,
    }: UGEProps,
    ref: ForwardedRef<UGERef>
) => {
    const artboardRef = useRef<ArtboardRef>(null)
    const [crop, setCrop] = useState<Position | undefined>()
    const [gifIds, setGifIds] = useState<string[]>([])

    useImperativeHandle(ref, () => ({
        createGIF: async () => {
            if (artboardRef.current) {
                return artboardRef.current.createGIF(crop)
            }

            return null
        },
        createSprite: async () => {
            if (artboardRef.current) {
                return artboardRef.current.createSprite(crop)
            }

            return null
        },
        getGifIds: () => {
            return gifIds
        },
    }))

    return (
        <ContextManager
            apiKey={apiKey}
            artboardRef={artboardRef}
            crop={crop}
            defaultEditor={defaultEditor}
            enableStickers={enableStickers}
            source={source}
            fetchFavoriteStickers={fetchFavoriteStickers}
            setCrop={setCrop}
            setGifIds={setGifIds}
            trackGAEvent={onGAEvent}
        >
            <PortalTooltipContext>
                <Container className={className}>
                    {closeable && <CloseButton onClick={onClose} />}
                    <Panel>
                        <Content>
                            <Artboard ref={artboardRef}>
                                <Overlay />
                            </Artboard>
                            <Editor />
                        </Content>
                        {children}
                    </Panel>
                    <Navigation />
                </Container>
            </PortalTooltipContext>
        </ContextManager>
    )
}

export default forwardRef(UGE)
